import { ResponsiveFunnel } from '@nivo/funnel';
import { differenceInDays, subDays } from 'date-fns';
import { FunctionComponent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserFunnelQuery } from '../../../../../services';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, StatsItem } from '../../helpers';
import { UserProps } from '../User';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-funnel';

export const UserFunnel: FunctionComponent<UserProps> = memo(({ from, to, platform }) => {
  const { t } = useTranslation();

  const { data: userFunnel, isFetching: isFetchingUserFunnel } = useUserFunnelQuery({
    from,
    to,
    platform,
  });

  const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

  const { data: userFunnelPrev, isFetching: isFetchingUserFunnelPrev } = useUserFunnelQuery({
    from: subDays(new Date(from), daysDiff).toISOString(),
    to: subDays(new Date(to), daysDiff).toISOString(),
    platform,
  });

  const { items = [] } = { ...userFunnel };

  const { items: itemsPrev = [] } = { ...userFunnelPrev };

  const renderStats = useMemo(() => {
    if (isFetchingUserFunnel || isFetchingUserFunnelPrev) {
      return <Loader stats />;
    }

    if (!userFunnel || !userFunnelPrev) {
      return null;
    }

    return (
      <div className={classes['analytics__chart-stats']}>
        {items.map(({ label, value }) => {
          return (
            <StatsItem
              key={label}
              label={label}
              localiseKey={`userFunnelLabels.${label}`}
              hintLocaliseKey={`userFunnelHints.${label}`}
              value={value}
              valuePrev={itemsPrev.find((dataset) => dataset.label === label)?.value ?? 0}
              isInline
            />
          );
        })}
      </div>
    );
  }, [
    isFetchingUserFunnel,
    isFetchingUserFunnelPrev,
    items,
    itemsPrev,
    userFunnel,
    userFunnelPrev,
  ]);

  const renderChart = useMemo(() => {
    if (isFetchingUserFunnel) {
      return <Loader />;
    }

    if (!userFunnel) {
      return null;
    }

    if (!items.length) {
      return <div className={classes['analytics__chart-no-records']}>{t('common.no-records')}</div>;
    }

    const data = items.map(({ label, value }) => {
      const localizedLabel = t(`userFunnelLabels.${label}`);

      return {
        id: localizedLabel,
        label: localizedLabel,
        value: Math.round((100 * value) / items[0]?.value),
      };
    });

    return (
      <div className={classes['analytics__chart-height']}>
        <ResponsiveFunnel
          ariaLabel={t(TYPE_LOCALISE_KEY)}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          data={data}
          colors={COLORS}
          direction={'horizontal'}
          labelColor={{ from: 'color', modifiers: [['darker', 3]] }}
          beforeSeparatorLength={100}
          afterSeparatorLength={100}
          beforeSeparatorOffset={20}
          afterSeparatorOffset={20}
          currentPartSizeExtension={10}
          valueFormat={(value) => `${value}%`}
          theme={{ labels: { text: { fontSize: 14 } } }}
        />
      </div>
    );
  }, [isFetchingUserFunnel, items, t, userFunnel]);

  return (
    <ChartWrapper typeLocaliseKey={TYPE_LOCALISE_KEY} infoLocaliseKey={'userFunnel.info'}>
      {renderStats}
      {renderChart}
    </ChartWrapper>
  );
});
